import { t } from '@lingui/core/macro'

type Change = [number, number, string, string]

interface IValueParams {
  cellValues: string[]
  changes: Change[]
}

export function updateChangesWithIncrementedValues({ cellValues, changes }: IValueParams) {
  const result: number[][] = []
  const arrOfIncrementedValues: string[] = []
  const NEW_VALUE_INDEX_POS = 3

  // Parse all the numbers out of the strings from the cell values
  // and store them in a 2D array so that we can check if they are sequential.
  // We do this to determine if the user is incrementing a sequence of numbers.
  // - The cell value may contain multiple numbers.
  // e.g. ['foo1', 'foo2', 'foo3'] => [[1, 2, 3]]
  // e.g. ['Campaign 1001', 'Campaign 1002', 'Campaign 1003'] => [[1001, 1002, 1003]]
  // e.g. ['Campaign 100 World 400', 'Campaign 200 World 500'] => [[100, 400], [200, 500]]
  for (const cellValue of cellValues) {
    const numbers = getNumbersFromString(cellValue) // get numbers to check if they are sequential

    for (const [j, number] of numbers.entries()) {
      if (!result[j]) {
        result[j] = []
      }

      result[j].push(number)
    }
  }

  // Check if the sequences are sized consistently, this would indicate that the user is incrementing.
  const isSequencesSizedConsistently = result.every(
    sequence => sequence.length === result[0]?.length,
  )

  // Loop through the 2d array of numbers to check if each group is sequential.
  // A sequential group of numbers means the user is incrementing a sequence of numbers,
  // so we can continue the sequence.
  // e.g. [[100, 200], [400, 500]] =>
  //        ['Campaign 300 World 600', 'Campaign 400 World 700', 'Campaign 500 World 800']
  // for (let i = 0, len = result.length; i < len; i++) {
  for (const [i, sequence] of result.entries()) {
    const latestValue = sequence[sequence.length - 1] ?? 0
    const { isSequential, difference } = isArraySequential(sequence)
    const doNotIncrementSequence = !isSequential || !isSequencesSizedConsistently

    if (doNotIncrementSequence) {
      continue // Not an incrementing sequence
    }

    let nextNum = latestValue // Start from the last number in the sequence

    // We're building the next incremental values based on the difference between the numbers
    // using the number of changes to determine how many incremental values to generate.
    for (const [j] of changes.entries()) {
      nextNum += difference
      const originalCellValue = cellValues[i] ?? ''

      // The position passed to replaceNumberAtPosition is critical here as it determines which number
      // in the string to replace.

      const lastUpdatedCellValue = arrOfIncrementedValues[j]
      const isValueAlreadySet = typeof lastUpdatedCellValue !== 'undefined'
      const cellValueToReplace = isValueAlreadySet ? lastUpdatedCellValue : originalCellValue

      arrOfIncrementedValues[j] = replaceNumberAtPosition(cellValueToReplace, i, nextNum.toString())
    }
  }

  if (arrOfIncrementedValues.length === 0) return changes

  // Update the changes cell value position with the incremented values.
  for (const [i, change] of changes.entries()) {
    const originalCellValue = cellValues[i] ?? ''
    const newValue = arrOfIncrementedValues[i] ?? ''
    change[NEW_VALUE_INDEX_POS] = newValue === '' ? originalCellValue : newValue
  }

  return changes
}

export function getCellValuesFromRange(startRow: number, endRow: number, column: number, hotTable) {
  const values: any[] = []

  for (let i = startRow; i <= endRow; i++) {
    values.push(hotTable.getDataAtCell(i, column))
  }

  return values
}

interface ArraySequentialResult {
  isSequential: boolean
  difference: number
}

export function isArraySequential(arr: number[]): ArraySequentialResult {
  if (arr.length <= 1) return { isSequential: false, difference: 0 }

  const difference = arr[1]! - arr[0]!

  if (difference === 0) return { isSequential: false, difference: 0 }

  // Check if the array is strictly increasing or decreasing by a consistent step
  const isSequential = arr.every((value, index) => {
    if (index === 0) return true // Skip the first element
    return value - arr[index - 1]! === difference
  })

  return { isSequential, difference: isSequential ? difference : 0 }
}

const numbersFromWordsPattern = /(?<=\b|_|-| )\d+(?=\b|_|-| )/g

export function getNumbersFromString(input: string): number[] {
  const numbers: number[] = []
  let match: RegExpExecArray | null

  // Use a while loop to iterate over all matches
  while ((match = numbersFromWordsPattern.exec(input)) !== null) {
    numbers.push(Number(match[0]))
  }

  return numbers
}

export function replaceNumberAtPosition(inputString: string, position: number, newNumber: string) {
  if (!inputString) return inputString

  // Find all numbers using the regex
  const numbers = inputString.match(numbersFromWordsPattern)

  // Check if the position is valid
  if (!numbers || position < 0 || position >= numbers.length) {
    throw new Error(t`Position out of range`)
  }

  let replacementCount = 0

  const result = inputString.replace(numbersFromWordsPattern, match => {
    if (replacementCount === position) {
      replacementCount += 1
      return newNumber
    }

    replacementCount += 1
    return match
  })

  return result
}
